import React, { useMemo } from 'react';
import type { Article, Settings } from '../../types';
import ArticleCard from '../../components/ArticleCard';
import { usePaginatedCollection } from '../../hooks/usePaginatedCollection';
import { useSEO } from '../../hooks/useSEO';


const ArticleCardSkeleton: React.FC = () => (
    <div className="group block animate-pulse">
        <div className="overflow-hidden mb-4 bg-gray-200 h-64"></div>
        <div className="h-3 bg-gray-300 rounded w-1/4 mb-3"></div>
        <div className="h-7 bg-gray-400 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-gray-300 rounded w-full mb-1"></div>
        <div className="h-4 bg-gray-300 rounded w-5/6"></div>
        <div className="h-4 bg-gray-400 rounded w-1/3 mt-4"></div>
    </div>
);

const JournalPage: React.FC<{ settings: Settings }> = ({ settings }) => {
    useSEO({
        title: `Journal | ${settings?.companyName || 'ARCH.'}`,
        description: `Read design stories, architectural highlights, news, and insights from the team at ${settings?.companyName || 'ARCH.'}.`,
        ogImage: settings?.openGraph?.imageUrl
    }, [settings]);

    const { 
        data: articles, 
        isLoading, 
        loadMore, 
        hasMore, 
        isLoadingMore 
    } = usePaginatedCollection<Article>(
        'articles', 
        null, // Remove where clause to avoid needing a composite index
        { field: 'publishDate', direction: 'desc' },
        6
    );

    // Filter for published articles on the client side for robustness
    const publishedArticles = useMemo(() => articles.filter(a => a.published), [articles]);

    return (
        <div className="bg-brand-light text-brand-dark pt-32 pb-16">
            <div className="container mx-auto px-6 max-w-6xl">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight font-heading">Journal</h1>
                    <p className="mt-4 text-lg text-gray-600 font-body">
                        Insights, inspiration, and reflections on architecture, design, and the built environment from our team.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                    {isLoading && Array.from({ length: 3 }).map((_, i) => <ArticleCardSkeleton key={i} />)}
                    {publishedArticles.map(article => (
                        <ArticleCard key={article.id} article={article} />
                    ))}
                </div>
                
                 {hasMore && (
                    <div className="text-center mt-16">
                        <button
                            onClick={loadMore}
                            disabled={isLoadingMore}
                            className="px-8 py-3 bg-brand-dark text-brand-light font-semibold uppercase tracking-widest hover:bg-gray-800 transition-colors disabled:bg-gray-400 font-header"
                        >
                            {isLoadingMore ? 'Loading...' : 'Load More Articles'}
                        </button>
                    </div>
                )}

                {!isLoading && publishedArticles.length === 0 && (
                     <p className="text-center text-gray-500 py-12">No journal articles have been published yet.</p>
                )}
            </div>
        </div>
    );
};

export default JournalPage;